'use client';

import { useCallback, useEffect, useState } from 'react';
import { RiArrowUpDownFill } from 'react-icons/ri';
import Flag from 'react-world-flags';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Skeleton } from '../../../components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { getCountryCode } from '../lib/utils';

interface CurrencyRate {
  charCode: string;
  nominal: number;
  name: string;
  value: number;
}

const ConverterSkeleton = () => (
  <section
    aria-busy="true"
    className="w-full sm:max-w-xl mx-auto px-2 sm:px-4 flex flex-col gap-4 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg p-6"
  >
    <Skeleton className="h-8 w-1/3 mb-1" />
    <Skeleton className="h-4 w-1/4 mb-4" />
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-1/3 mb-1" />
      <div className="flex gap-2">
        <Skeleton className="h-10 flex-grow" />
        <Skeleton className="h-10 w-[120px]" />
      </div>
    </div>
    <div className="flex justify-center">
      <Skeleton className="h-10 w-10 rounded-full" />
    </div>
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-1/3 mb-1" />
      <div className="flex gap-2">
        <Skeleton className="h-10 flex-grow" />
        <Skeleton className="h-10 w-[120px]" />
      </div>
    </div>
  </section>
);

function ensureRUB(rates: CurrencyRate[]): CurrencyRate[] {
  const hasRuble = rates.some((r) => r.charCode === 'RUB');
  if (hasRuble) return rates;
  return [{ charCode: 'RUB', nominal: 1, name: 'Российский рубль', value: 1 }, ...rates];
}

function parseAmount(s: string) {
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

export function CurrencyConverter({ initialRates = [] }: { initialRates?: CurrencyRate[] }) {
  const [amountFrom, setAmountFrom] = useState('1');
  const [amountTo, setAmountTo] = useState('');
  const [selectedCurrencyFrom, setSelectedCurrencyFrom] = useState('USD');
  const [selectedCurrencyTo, setSelectedCurrencyTo] = useState('RUB');

  const [currencyRates, setCurrencyRates] = useState<CurrencyRate[]>(
    initialRates.length ? ensureRUB(initialRates) : [],
  );

  const [loading, setLoading] = useState<boolean>(initialRates.length ? false : true);
  const [error, setError] = useState<string | null>(null);

  // какой инпут последний меняли
  const [lastChangedInput, setLastChangedInput] = useState<'from' | 'to'>('from');
  const [mounted, setMounted] = useState(false);

  // useEffect(() => {
  //   setMounted(true);
  // }, []);

  // if (!mounted) return <ConverterSkeleton />;
  // ✅ O(1) доступ вместо find() по массиву
  const ratesByCode = new Map<string, CurrencyRate>();
  for (const rate of currencyRates) {
    ratesByCode.set(rate.charCode, rate);
  }

  // (опционально) лёгкое обновление в фоне раз в заход (не обязательно)
  // Важно: НЕ дергать часто.
  useEffect(() => {
    if (initialRates.length) return; // если уже пришло с сервера — можно вообще не обновлять

    const controller = new AbortController();

    async function fetchCurrencyRates() {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch('/api/finance/cb-rates', {
          signal: controller.signal,
          cache: 'force-cache',
        });
        if (!res.ok) throw new Error('Не удалось загрузить курсы валют.');
        const data = await res.json();
        const rates: CurrencyRate[] = ensureRUB(data.rates || []);
        setCurrencyRates(rates);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Ошибка загрузки курсов:', err);
        setError(
          err instanceof Error ? err.message : 'Произошла ошибка при загрузке курсов валют.',
        );
      } finally {
        setLoading(false);
      }
    }

    fetchCurrencyRates();

    return () => controller.abort();
  }, [initialRates.length]);

  const convert = useCallback(
    (amountStr: string, fromCode: string, toCode: string) => {
      const rateFrom = ratesByCode.get(fromCode);
      const rateTo = ratesByCode.get(toCode);
      if (!rateFrom || !rateTo) return '';

      const amount = parseAmount(amountStr);
      if (!Number.isFinite(amount)) return '';

      const amountInRub = (amount * rateFrom.value) / rateFrom.nominal;
      const converted = (amountInRub * rateTo.nominal) / rateTo.value;

      if (!Number.isFinite(converted)) return '';
      return converted.toFixed(4);
    },
    [ratesByCode],
  );

  // ✅ один эффект вместо двух
  useEffect(() => {
    if (currencyRates.length === 0) return;

    if (lastChangedInput === 'from') {
      setAmountTo(convert(amountFrom, selectedCurrencyFrom, selectedCurrencyTo));
    } else {
      setAmountFrom(convert(amountTo, selectedCurrencyTo, selectedCurrencyFrom));
    }
  }, [
    amountFrom,
    amountTo,
    selectedCurrencyFrom,
    selectedCurrencyTo,
    lastChangedInput,
    convert,
    currencyRates.length,
  ]);

  const swapCurrencies = useCallback(() => {
    setSelectedCurrencyFrom(selectedCurrencyTo);
    setSelectedCurrencyTo(selectedCurrencyFrom);
    setAmountFrom(amountTo);
    setAmountTo(amountFrom);
    setLastChangedInput('from');
  }, [amountFrom, amountTo, selectedCurrencyFrom, selectedCurrencyTo]);

  const getExchangeRateDisplay = useCallback(
    (fromChar: string, toChar: string) => {
      const rateFrom = ratesByCode.get(fromChar);
      const rateTo = ratesByCode.get(toChar);
      if (!rateFrom || !rateTo || rateFrom.value === 0 || rateTo.value === 0) return 'Недоступно';

      const rate = rateFrom.value / rateFrom.nominal / (rateTo.value / rateTo.nominal);
      return `1 ${fromChar} = ${rate.toFixed(4)} ${toChar}`;
    },
    [ratesByCode],
  );

  const sortedRates = [...currencyRates].sort((a, b) => {
    const rank = (code: string) =>
      code === 'RUB' ? 0 : code === 'USD' ? 1 : code === 'EUR' ? 2 : 99;
    const ra = rank(a.charCode);
    const rb = rank(b.charCode);
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name, 'ru');
  });

  const isLoading = loading && currencyRates.length === 0;

  if (isLoading) return <ConverterSkeleton />;

  if (error) {
    return (
      <section role="alert" className="p-6 text-red-500 text-center">
        <p>Ошибка: {error}</p>
      </section>
    );
  }

  return (
    <section
      aria-label="Конвертер валют"
      className="w-full p-4 sm:p-6 sm:max-w-xl mx-auto flex flex-col gap-3 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg"
    >
      <h2 className="text-2xl font-bold text-main">Конвертер валют</h2>
      <p className="text-gray-400 text-sm -mt-2">по курсу ЦБ РФ</p>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          <label htmlFor="amount-from" className="text-sm font-medium text-gray-300">
            Из
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="amount-from"
              type="number"
              min="0"
              value={amountFrom}
              onChange={(e) => {
                setAmountFrom(e.target.value);
                setLastChangedInput('from');
              }}
              className="flex-grow bg-slate-900 border-slate-700 text-white text-lg focus:ring-blue-500"
            />
            <Select
              value={selectedCurrencyFrom}
              onValueChange={(value) => {
                setSelectedCurrencyFrom(value);
                setLastChangedInput('from');
              }}
            >
              <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-white focus:ring-blue-500">
                <SelectValue placeholder="Валюта" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600 text-white max-h-[300px] overflow-y-auto">
                {sortedRates.map((currency) => (
                  <SelectItem
                    key={`from-${currency.charCode}`}
                    value={currency.charCode}
                    className="hover:bg-gray-600 focus:bg-gray-600"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <Flag
                        code={getCountryCode(currency.charCode)}
                        height={16}
                        width={24}
                        className="rounded-sm border border-slate-500 flex-shrink-0"
                      />
                      <span className="font-medium flex-shrink-0">{currency.charCode}</span>
                      <span className="text-gray-400 text-xs truncate ml-1">{currency.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-gray-400 h-4 ml-1">
            {amountFrom && Number.isFinite(parseAmount(amountFrom))
              ? getExchangeRateDisplay(selectedCurrencyFrom, selectedCurrencyTo)
              : ''}
          </p>
        </div>

        <div className="flex justify-center my-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={swapCurrencies}
                variant="ghost"
                className="text-blue-500 hover:text-blue-400 hover:bg-slate-700 rounded-full p-3 transition-transform duration-200 hover:scale-110"
                aria-label="Поменять местами валюты"
              >
                <RiArrowUpDownFill className="h-6 w-6" />
              </Button>
            </TooltipTrigger>

            <TooltipContent side="top" className="bg-gray-800 text-white border border-gray-600">
              Поменять валюты местами
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="amount-to" className="text-sm font-medium text-gray-300">
            В
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="amount-to"
              type="number"
              min="0"
              value={amountTo}
              onChange={(e) => {
                setAmountTo(e.target.value);
                setLastChangedInput('to');
              }}
              className="flex-grow bg-slate-900 border-slate-700 text-white text-lg focus:ring-blue-500"
            />
            <Select
              value={selectedCurrencyTo}
              onValueChange={(value) => {
                setSelectedCurrencyTo(value);
                setLastChangedInput('to');
              }}
            >
              <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-white focus:ring-blue-500">
                <SelectValue placeholder="Валюта" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600 text-white max-h-[300px] overflow-y-auto">
                {sortedRates.map((currency) => (
                  <SelectItem
                    key={`to-${currency.charCode}`}
                    value={currency.charCode}
                    className="hover:bg-gray-600 focus:bg-gray-600"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <Flag
                        code={getCountryCode(currency.charCode)}
                        height={16}
                        width={24}
                        className="rounded-sm border border-slate-500 flex-shrink-0"
                      />
                      <span className="font-medium flex-shrink-0">{currency.charCode}</span>
                      <span className="text-gray-400 text-xs truncate ml-1">{currency.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-gray-400 h-4 ml-1">
            {amountTo && Number.isFinite(parseAmount(amountTo))
              ? getExchangeRateDisplay(selectedCurrencyTo, selectedCurrencyFrom)
              : ''}
          </p>
        </div>
      </div>
    </section>
  );
}
